import {
  GetUserQuery,
  ListUsersQuery,
  UpdateUserInput,
  UpdateUserMutation,
} from '@akin-home/amplify';
import { handleImageFieldsOnPost } from '@akin-home/images/frontend';
import { UserModel } from '@akin-home/user';
import {
  updateUserMutation,
  userModelToUpdateGraphql,
} from '@akin-home/users/frontend';
import { InfiniteData, useMutation, useQueryClient } from 'react-query';
import { userQueryKeys } from './index';

type CachedUser = NonNullable<GetUserQuery['getUser']>;
type CachedUsers = NonNullable<ListUsersQuery['listUsers']>['items'];

export const useUpdateUser = () => {
  const client = useQueryClient();

  return useMutation(updateUserMutation, {
    onSuccess({ data }) {
      if (!data.updateUser) return;

      const item = data.updateUser;

      client.setQueriesData<CachedUsers | undefined>(
        userQueryKeys.lists(),
        (data) => data?.map((user) => (user?.id === item.id ? item : user))
      );

      client.setQueryData<CachedUser | undefined>(
        userQueryKeys.detail(item.id),
        (user) => user && item
      );

      client.setQueriesData<InfiniteData<ListUsersQuery>>(
        userQueryKeys.all_pages(),
        setPagedQueries(item)
      );
    },
  });
};

export const useUpdateUserQuick = () => {
  const client = useQueryClient();

  return useMutation(updateUserMutation, {
    onMutate({ input }) {
      if (!input) return;

      client.setQueriesData<CachedUsers | undefined>(
        userQueryKeys.lists(),
        (data) =>
          data?.map((user) =>
            user?.id === input.id
              ? { ...user, ...(input as Partial<CachedUser>) }
              : user
          )
      );

      client.setQueryData<CachedUser | undefined>(
        userQueryKeys.detail(input.id),
        (user) => user && { ...user, ...(input as Partial<CachedUser>) }
      );

      client.setQueriesData<InfiniteData<ListUsersQuery>>(
        userQueryKeys.all_pages(),
        optimisticSetPagedQueries(input)
      );
    },
  });
};

export const useUpdateUserModel = () => {
  const update = useUpdateUser();

  return async (user: UserModel) => {
    const user_with_image_fields = await handleImageFieldsOnPost(user as any, {
      habitatID: user.habitatID,
    });

    return await update.mutateAsync({
      input: userModelToUpdateGraphql(user_with_image_fields as UserModel),
    });
  };
};

const setPagedQueries =
  (input: UpdateUserMutation['updateUser']) =>
  (data?: InfiniteData<ListUsersQuery>): any => ({
    ...data,
    pageParams: data?.pageParams as string[],
    pages: data
      ? data.pages.map((page) => ({
          ...page,
          listUsers: {
            ...page.listUsers,
            items: page.listUsers?.items.map((user) =>
              user && user.id === input?.id ? input : user
            ),
            __typename: 'ModelUserConnection',
          },
        }))
      : [],
  });

const optimisticSetPagedQueries =
  (input: UpdateUserInput) =>
  (data?: InfiniteData<ListUsersQuery>): any => ({
    ...data,
    pageParams: data?.pageParams as string[],
    pages: data
      ? data.pages.map((page) => ({
          ...page,
          listUsers: {
            ...page.listUsers,
            items: page.listUsers?.items.map((user) =>
              user && user.id === input?.id ? input : user
            ),
            __typename: 'ModelUserConnection',
          },
        }))
      : [],
  });
